'use client'

import { Button, Text } from '@/components'
import { Header } from '@/modules/crawler'
import { appSelectors, Process, useAppStore } from '@/store'
import { useTranslations } from 'next-intl'
import { useParams, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo } from 'react'
import { Else, If, Then, When } from 'react-if'
import { Download } from './Download'
import { DetailBox } from './DetailBox'
import { cn } from '@/lib/utils'
import { SitePageItemDtoType } from '@/config/api-generated/model'
import { ClientRoutes, Role } from '@/config'
import { useLocalizedRoutes } from '@/lib/hooks'

export const ProcessDetails = () => {
  const { id } = useParams<{ id: string }>()
  const t = useTranslations('process')
  const processes = useAppStore(appSelectors.processes)
  const { user } = useAppStore(appSelectors.auth)
  const { setSetting, removeProcess } = useAppStore(appSelectors.actions)
  const asyncActions = useAppStore(appSelectors.asyncActions)
  const router = useRouter()
  const { get } = useLocalizedRoutes()

  const process: Partial<Process> | null = useMemo(
    () => processes?.[id] ?? null,
    [id, processes],
  )

  const inProgress = useMemo(
    () =>
      typeof process?.isFinished !== 'undefined' &&
      process?.isFinished !== true,
    [process],
  )

  const isAdmin = useMemo(() => user?.roles.includes(Role.ADMIN), [user])

  const onAbortOrRestart = useCallback(async () => {
    if (inProgress) {
      await asyncActions.abortProcess(id)
    } else if (process && isAdmin) {
      await asyncActions.deleteProcess(id)

      setSetting(process?.settings ?? null)
      const newId = await asyncActions.run(process?.url as string, true)
      if (newId) {
        router.push(newId)
      }
    } else {
      router.push(get(ClientRoutes.Home))
    }
  }, [isAdmin, asyncActions, id, inProgress, process, router, setSetting, get])

  const counts: Record<PropertyKey, number | undefined> = useMemo(
    () => ({
      [SitePageItemDtoType.Internal]: process?.pages?.items.filter(
        (item) => item.type === SitePageItemDtoType.Internal,
      )?.length,
      [SitePageItemDtoType.External]: process?.settings?.advanced
        ?.includeExternal
        ? process?.pages?.items.filter(
            (item) => item.type === SitePageItemDtoType.External,
          )?.length
        : undefined,
      [SitePageItemDtoType.File]: process?.settings?.advanced?.includeFiles
        ? process?.pages?.items.filter(
            (item) => item.type === SitePageItemDtoType.File,
          )?.length
        : undefined,
      [SitePageItemDtoType.Image]: process?.settings?.advanced?.includeImages
        ? process?.pages?.items.filter(
            (item) => item.type === SitePageItemDtoType.Image,
          )?.length
        : undefined,
      [SitePageItemDtoType.Orphan]: process?.settings?.advanced?.includeOrphan
        ? process?.pages?.items.filter(
            (item) => item.type === SitePageItemDtoType.Orphan,
          )?.length
        : undefined,
      Broken: process?.settings?.advanced?.includeBroken
        ? process?.pages?.items.filter(
            (item) => item?.status && (item?.status?.code ?? 0) > 400,
          )?.length
        : undefined,
    }),
    [process],
  )

  useEffect(() => {
    if (user && !process && id) {
      asyncActions.getSite(id)
    }
  }, [asyncActions, id, process, user])

  useEffect(
    () => () => {
      removeProcess(id)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <div
      className={cn(
        'w-full h-full flex-1',
        'bg-waves relative',
        'w-full h-full bg-no-repeat bg-cover bg-local pt-6 bg-position-[top_100px_center]',
      )}
    >
      <div className="xl:max-w-4xl mx-auto my-24 text-white">
        <Header />

        <div className="loader text-center">
          <div className="w-[25vh] h-[25vh] min-w-[250px] min-h-[250px] max-w-[300px] max-h-[300px] mx-auto mt-12 mb-6 shadow-[0_0_95px_rgba(0,0,0,0.25)] rounded-full p-0 bg-white/2 border-10 border-gray-600/60 relative">
            <When condition={inProgress}>
              <div className="gradient-border w-[calc(100%-1px)] h-[calc(100%-1px)] border-2 rounded-full animate-spin" />
            </When>
            <div className="w-[calc(100%-10px)] h-[calc(100%-10px)] top-[5px] left-[5px] absolute z-2 rounded-full animate-spin bg-primary"></div>
            <Button
              onClick={onAbortOrRestart}
              variant={inProgress ? 'default' : 'glow'}
              className="w-1/2 h-1/2 absolute top-1/2 left-1/2 -translate-x-1/2 z-3 -translate-y-1/2 rounded-full bg-glow/5 hover:bg-glow/10 text-2xl text-center flex justify-center items-center"
            >
              <If condition={inProgress}>
                <Then>{t('actions.stop')}</Then>
                <Else>
                  <If condition={isAdmin}>
                    <Then>{t('actions.restart')}</Then>
                    <Else>{t('actions.new')}</Else>
                  </If>
                </Else>
              </If>
            </Button>
          </div>
          <When condition={inProgress}>
            <Text variant="heading">{t('inProgress')}</Text>
            <div className="w-full rounded-full bg-glow/5 text-center py-2 px-3 mt-3 text-white/50">
              {process?.url?.replace(/https?:\/\//gi, '') ?? '-'}
            </div>
          </When>
          <When condition={!inProgress}>
            <div className="flex gap-3 justify-center w-full">
              <Text variant="heading" className="gradient-text">
                {t('complete')}
              </Text>
              <Text variant="heading">
                {process?.pageCount ?? process?.pages?.items?.length ?? 0}{' '}
                {t('linksFound')}
              </Text>
            </div>

            <Download id={id} />
          </When>

          <div className="w-full h-[2px] my-5 bg-white/10" />

          <div className="flex gap-3 justify-evenly max-lg:px-3 max-md:flex-col">
            <DetailBox
              loading={inProgress}
              title={t('details.internal')}
              count={counts?.[SitePageItemDtoType.Internal] ?? 'N/A'}
            />
            <DetailBox
              loading={inProgress}
              title={t('details.external')}
              count={counts?.[SitePageItemDtoType.External] ?? 'N/A'}
            />
            <DetailBox
              loading={inProgress}
              title={t('details.broken')}
              count={counts.Broken ?? 'N/A'}
            />
            <DetailBox
              loading={inProgress}
              title={t('details.orphan')}
              count={counts?.[SitePageItemDtoType.Orphan] ?? 'N/A'}
            />
            <DetailBox
              loading={inProgress}
              title={t('details.file')}
              count={counts?.[SitePageItemDtoType.File] ?? 'N/A'}
            />
            <DetailBox
              loading={inProgress}
              title={t('details.image')}
              count={counts?.[SitePageItemDtoType.Image] ?? 'N/A'}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
